(function () {
  'use strict';

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const STAR_PATH = 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z';
  const PLAY_PATH = 'M8 5.5v13l10.5-6.5L8 5.5Z';
  const CLOSE_PATH = 'm6 6 12 12M18 6 6 18';
  const ARROW_PATH = 'M3 12h18m-6-6 6 6-6 6';
  const SIZE_LABELS = { '100ml': '100 ml', '200ml': '200 ml' };
  const feeds = new Map();
  let viewer;

  /** Creates an element with an optional class and text content. */
  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  }

  /** Draws a 24-unit icon; filled icons use the fill colour, line icons the stroke. */
  function icon(pathData, filled) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', filled ? 'nr-icon nr-icon--fill' : 'nr-icon');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', pathData);
    svg.append(path);
    return svg;
  }

  /** Fetches a review feed once per page and shares the result between sections. */
  function loadFeed(address, page = 1) {
    const url = new URL(address, window.location.href);
    if (page > 1) url.searchParams.set('page', String(page));
    const key = url.href;
    if (!feeds.has(key)) {
      feeds.set(key, fetch(key, { credentials: 'omit' })
        .then((response) => {
          if (!response.ok) throw new Error('The reviews could not be loaded.');
          return response.json();
        })
        .then((data) => ({ ...data, feedUrl: key })));
    }
    return feeds.get(key);
  }

  /** Shows a rating as stars filled to the exact value, with the number given to assistive technology. */
  function rating(value) {
    const wrap = el('span', 'nr-rating');
    const stars = el('span', 'nr-stars');
    stars.setAttribute('aria-hidden', 'true');
    const empty = el('span', 'nr-stars__row nr-stars__row--empty');
    const filled = el('span', 'nr-stars__row nr-stars__row--filled');
    for (let star = 1; star <= 5; star += 1) {
      empty.append(icon(STAR_PATH, true));
      filled.append(icon(STAR_PATH, true));
    }
    filled.style.width = `${(Math.min(5, Math.max(0, value)) / 5) * 100}%`;
    stars.append(empty, filled);
    wrap.append(stars, el('span', 'visually-hidden', `Rated ${Math.round(value * 10) / 10} out of 5`));
    return wrap;
  }

  /** Formats an ISO date for a review, either in full or as month and year. */
  function formatDate(iso, short) {
    const options = short ? { year: 'numeric', month: 'long' } : { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(iso).toLocaleDateString('en-CA', options);
  }

  /** Builds the review count text, such as "7 reviews". */
  function reviewCount(count) {
    return `${count} ${count === 1 ? 'review' : 'reviews'}`;
  }

  /** Creates the image, or a paused video showing its first frame, for one attachment. */
  function mediaElement(item, feedUrl) {
    const address = new URL(item.url, feedUrl).href;
    if (item.kind === 'video') {
      const video = el('video');
      video.src = `${address}#t=0.1`;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('aria-hidden', 'true');
      return video;
    }
    const image = el('img');
    image.src = address;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  /** Creates a button showing one attachment that opens it in the viewer. */
  function mediaButton(entries, index, className, feedUrl) {
    const { review, item } = entries[index];
    const button = el('button', className);
    button.type = 'button';
    button.setAttribute('aria-label', `${item.kind === 'video' ? 'Play the video' : 'View the photo'} from ${review.name}’s review`);
    button.append(mediaElement(item, feedUrl));
    if (item.kind === 'video') {
      const badge = el('span', 'nr-play');
      badge.append(icon(PLAY_PATH, true), el('span', '', 'Video'));
      button.append(badge);
    }
    button.addEventListener('click', () => openViewer(entries, index, feedUrl));
    return button;
  }

  /** Lists every attachment of a review as viewer entries. */
  function entriesFor(review) {
    return review.media.map((item) => ({ review, item }));
  }

  /** Writes the reviewer details line, such as "Maya · 200 ml · October 2026". */
  function details(review, withDate) {
    const parts = [review.name, SIZE_LABELS[review.bottleSize]];
    if (withDate) parts.push(formatDate(review.createdAt, true));
    return parts.filter(Boolean).join(' · ');
  }

  /** Creates the shared viewer dialog the first time an attachment is opened. */
  function ensureViewer() {
    if (viewer) return viewer;
    const dialog = el('dialog', 'nr-viewer');
    dialog.setAttribute('aria-label', 'Photo or video from a review');
    const frame = el('div', 'nr-viewer__frame');
    const stage = el('div', 'nr-viewer__stage');
    const caption = el('div', 'nr-viewer__caption');
    const close = el('button', 'nr-viewer__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.append(icon(CLOSE_PATH));
    const nav = el('div', 'nr-viewer__nav');
    const previous = el('button', 'nr-viewer__arrow nr-viewer__arrow--previous');
    previous.type = 'button';
    previous.setAttribute('aria-label', 'Previous');
    previous.append(icon(ARROW_PATH));
    const next = el('button', 'nr-viewer__arrow');
    next.type = 'button';
    next.setAttribute('aria-label', 'Next');
    next.append(icon(ARROW_PATH));
    const position = el('span', 'nr-viewer__position');
    nav.append(previous, position, next);
    frame.append(stage, caption, close);
    dialog.append(frame);
    document.body.append(dialog);
    viewer = { dialog, stage, caption, nav, previous, next, position, entries: [], index: 0, feedUrl: '' };
    close.addEventListener('click', () => dialog.close());
    previous.addEventListener('click', () => showEntry(viewer.index - 1));
    next.addEventListener('click', () => showEntry(viewer.index + 1));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showEntry(viewer.index - 1);
      if (event.key === 'ArrowRight') showEntry(viewer.index + 1);
    });
    dialog.addEventListener('close', () => viewer.stage.replaceChildren());
    return viewer;
  }

  /** Shows one attachment large, next to the review it belongs to. */
  function showEntry(index) {
    if (index < 0 || index >= viewer.entries.length) return;
    viewer.index = index;
    const { review, item } = viewer.entries[index];
    const address = new URL(item.url, viewer.feedUrl).href;
    let media;
    if (item.kind === 'video') {
      media = el('video');
      media.src = address;
      media.controls = true;
      media.playsInline = true;
      media.autoplay = true;
      media.setAttribute('aria-label', `Video from ${review.name}’s review`);
    } else {
      media = el('img');
      media.src = address;
      media.alt = `Photo from ${review.name}’s review`;
    }
    viewer.stage.replaceChildren(media);
    viewer.caption.replaceChildren(
      rating(review.rating),
      el('h3', 'nr-viewer__title', review.title),
      el('p', 'nr-viewer__text', review.body),
      el('p', 'nr-viewer__details', details(review, true)),
      viewer.nav,
    );
    viewer.nav.hidden = viewer.entries.length < 2;
    viewer.position.textContent = `${index + 1} of ${viewer.entries.length}`;
    viewer.previous.disabled = index === 0;
    viewer.next.disabled = index === viewer.entries.length - 1;
  }

  /** Opens the viewer on a list of attachments, starting at one of them. */
  function openViewer(entries, index, feedUrl) {
    const current = ensureViewer();
    current.entries = entries;
    current.feedUrl = feedUrl;
    showEntry(index);
    if (!current.dialog.open) current.dialog.showModal();
  }

  /** Fills the average rating and review count. */
  function renderScore(target, summary) {
    if (!target) return;
    const number = el('p', 'nr-score__number', Number(summary.average).toFixed(1));
    const text = el('div', 'nr-score__text');
    text.append(rating(summary.average), el('p', 'nr-score__meta', `Based on ${reviewCount(summary.count)}`));
    target.replaceChildren(number, text);
  }

  /** Draws how many reviews gave each star rating, when the feed provides it. */
  function renderBars(target, summary) {
    if (!target || !summary.distribution) return;
    const list = el('dl', 'nr-bars');
    for (let star = 5; star >= 1; star -= 1) {
      const count = Number(summary.distribution[star] || 0);
      const row = el('div', 'nr-bar');
      const label = el('dt', 'nr-bar__label', star);
      label.append(icon(STAR_PATH, true), el('span', 'visually-hidden', star === 1 ? ' star' : ' stars'));
      const value = el('dd', 'nr-bar__value');
      const track = el('span', 'nr-bar__track');
      const fill = el('span', 'nr-bar__fill');
      fill.style.width = `${summary.count ? (count / summary.count) * 100 : 0}%`;
      track.append(fill);
      value.append(track, el('span', 'nr-bar__count', count), el('span', 'visually-hidden', count === 1 ? ' review' : ' reviews'));
      row.append(label, value);
      list.append(row);
    }
    target.replaceChildren(list);
  }

  /** Builds a homepage card: a photo or video on top when there is one, otherwise the words lead. */
  function homeCard(review, feedUrl) {
    const entries = entriesFor(review);
    const card = el('article', `nr-card ${entries.length ? 'nr-card--media' : 'nr-card--text'}`);
    if (entries.length) {
      const media = mediaButton(entries, 0, 'nr-card__media', feedUrl);
      if (entries.length > 1) media.append(el('span', 'nr-card__more', `+${entries.length - 1}`));
      card.append(media);
    }
    const body = el('div', 'nr-card__body');
    body.append(
      rating(review.rating),
      el('h3', 'nr-card__title', review.title),
      el('p', 'nr-card__text', review.body),
      el('p', 'nr-card__details', details(review, true)),
    );
    card.append(body);
    return card;
  }

  /** Builds a full review for the product page, with every attachment and any reply. */
  function productItem(review, feedUrl) {
    const item = el('article', 'nr-item');
    const top = el('div', 'nr-item__top');
    const date = el('time', 'nr-item__date', formatDate(review.createdAt));
    date.dateTime = review.createdAt;
    top.append(rating(review.rating), date);
    item.append(top, el('h3', 'nr-item__title', review.title), el('p', 'nr-item__text', review.body));
    const entries = entriesFor(review);
    if (entries.length) {
      const media = el('div', 'nr-item__media');
      entries.forEach((entry, index) => media.append(mediaButton(entries, index, 'nr-thumb', feedUrl)));
      item.append(media);
    }
    item.append(el('p', 'nr-item__details', details(review, false)));
    if (review.reply) {
      const reply = el('div', 'nr-reply');
      reply.append(el('p', 'nr-reply__label', 'Reply from Neloura Baby'), el('p', 'nr-reply__text', review.reply));
      item.append(reply);
    }
    return item;
  }

  /** Shows the newest reviews as highlights, and keeps the section hidden until there are enough of them. */
  function initHome(section, data) {
    if (!data.summary.count || data.summary.count < (Number(section.dataset.minimum) || 1)) return;
    const limit = Number(section.dataset.limit) || 3;
    renderScore(section.querySelector('[data-reviews-score]'), data.summary);
    section.querySelector('[data-reviews-list]').replaceChildren(...data.reviews.slice(0, limit).map((review) => homeCard(review, data.feedUrl)));
    section.hidden = false;
  }

  /** Shows the full review list with a summary, a photo and video gallery, a filter and paging. */
  function initProduct(section, data) {
    const limit = Number(section.dataset.limit) || 5;
    const list = section.querySelector('[data-reviews-list]');
    const more = section.querySelector('[data-reviews-more]');
    const filters = section.querySelector('[data-reviews-filters]');
    const gallery = section.querySelector('[data-reviews-gallery]');
    const reviews = [...data.reviews];
    let page = data.page || 1;
    let hasMore = Boolean(data.hasMore);
    let shown = limit;
    let filter = 'all';
    if (!data.summary.count) {
      section.querySelector('[data-reviews-score]').replaceChildren(el('p', 'nr-score__empty', 'No reviews yet.'));
      list.replaceChildren(el('p', 'nr-status', 'Be the first to tell other parents how it’s going.'));
      return;
    }
    renderScore(section.querySelector('[data-reviews-score]'), data.summary);
    renderBars(section.querySelector('[data-reviews-bars]'), data.summary);

    function visible() {
      return filter === 'media' ? reviews.filter((review) => review.media.length) : reviews;
    }

    function render() {
      const matches = visible();
      list.replaceChildren(...matches.slice(0, shown).map((review) => productItem(review, data.feedUrl)));
      more.hidden = !(matches.length > shown || (filter === 'all' && hasMore));
      const allMedia = reviews.flatMap(entriesFor);
      gallery.hidden = !allMedia.length;
      filters.hidden = !allMedia.length;
      section.querySelector('[data-count="all"]').textContent = String(data.summary.count);
      section.querySelector('[data-count="media"]').textContent = String(reviews.filter((review) => review.media.length).length);
      const strip = section.querySelector('[data-reviews-gallery-strip]');
      strip.replaceChildren(...allMedia.slice(0, 8).map((entry, index) => mediaButton(allMedia, index, 'nr-thumb', data.feedUrl)));
    }

    filters.querySelectorAll('[data-filter]').forEach((button) => button.addEventListener('click', () => {
      filter = button.dataset.filter;
      shown = limit;
      filters.querySelectorAll('[data-filter]').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
      render();
    }));

    more.addEventListener('click', async () => {
      shown += limit;
      if (filter === 'all' && shown > reviews.length && hasMore) {
        more.disabled = true;
        try {
          const next = await loadFeed(section.dataset.feed, page + 1);
          page = next.page;
          hasMore = Boolean(next.hasMore);
          reviews.push(...next.reviews);
        } catch {
          hasMore = false;
        } finally {
          more.disabled = false;
        }
      }
      render();
    });

    render();
  }

  /** Fills the star line under the product title, and leaves it hidden when there are no reviews. */
  function initSummaryLine(link, data) {
    const { count, average } = data.summary;
    if (!count) return;
    link.replaceChildren(
      rating(average),
      el('span', 'nr-rating-line__average', Number(average).toFixed(1)),
      el('span', 'nr-rating-line__count', reviewCount(count)),
    );
    link.hidden = false;
  }

  /** Connects every reviews section and star line on the page, once each. */
  function init(scope = document) {
    scope.querySelectorAll('[data-neloura-reviews]').forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      loadFeed(section.dataset.feed)
        .then((data) => (section.dataset.layout === 'product' ? initProduct(section, data) : initHome(section, data)))
        .catch(() => {
          section.hidden = true;
        });
    });
    scope.querySelectorAll('[data-neloura-review-summary]').forEach((link) => {
      if (link.dataset.initialized) return;
      link.dataset.initialized = 'true';
      loadFeed(link.dataset.feed).then((data) => initSummaryLine(link, data)).catch(() => {
        link.hidden = true;
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
